import _extends from "@babel/runtime/helpers/extends";
var _DomainActionsErrorsM, _DomainActionsErrorsM2, _DomainActionsErrorsM3;
import { Box } from '@wix/design-system';
import { DomainActionsErrorsMap, DomainSearchSuiteModule, SearchRecommendationModule } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import clsx from 'clsx';
import React from 'react';
import { GeneralAnonymousOverride, SubdomainRootTaken } from '../../../Components';
import { AnonymousFooter } from '../../../Components/AnonymousFooter/AnonymousFooter';
import { AnonymousResultsHeader } from '../../../Components/Headers';
import { BackButton } from '../../../Components/Headers/HeaderWrapper';
import { AnonymousVoucherBanner } from '../../../Components/VoucherBanner';
import { WIX_HEADER_HEIGHT } from '@wix/core-domain-search';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useIsWixHpReferral } from '../../../hooks/useIsWixHpReferral';
import { useRecommendations } from '../../../hooks/useRecommendations';
import { useResultsBack } from '../../../hooks/useResultsBack';
import { useResultsPageConfig } from '../../../hooks/useResultsPageConfig';
import { useSettledMethods } from '../../../hooks/useSettledMethod';
import { ANONYMOUS_SETTLEMENTS } from '../../../utils/anonymousSettlements';
import { ResultsLayout, ResultsRecommendations, ResultsSearchBar } from '../../ResultsLayout';
import { ExperimentSpecs } from '../../../constants';
import { DeclaredExperimentsProvider } from '../../../hooks/declaredExperiments';
import { usePerceptionEnabled, usePerceptionPricing } from '../../../pricingPerception';
import s from '../../anonymousResultsPage.scss';
const NO_PERCEPTION = {};
const customConfiguration = {
  ...(((_DomainActionsErrorsM = DomainActionsErrorsMap.connect) == null ? void 0 : _DomainActionsErrorsM.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM2 = DomainActionsErrorsMap.assign) == null ? void 0 : _DomainActionsErrorsM2.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  ...(((_DomainActionsErrorsM3 = DomainActionsErrorsMap.transfer) == null ? void 0 : _DomainActionsErrorsM3.reduce((acc, status) => ({
    ...acc,
    [status]: GeneralAnonymousOverride
  }), {})) ?? {}),
  transfer: GeneralAnonymousOverride,
  connect: GeneralAnonymousOverride,
  subdomainRootIsNotConnected: SubdomainRootTaken,
  domainTakenInWix: GeneralAnonymousOverride,
  subdomainTakenInWix: SubdomainRootTaken,
  noStatus: GeneralAnonymousOverride
};
export const AnonymousResultsPage = _ref => {
  let {
    handlers,
    externalViewer,
    deviceType,
    showCartHeader
  } = _ref;
  useSettledMethods(ANONYMOUS_SETTLEMENTS);
  // Declared here rather than in the content component: the pricing call happens inside
  // ResultsLayout, which Content renders.
  const isMobile = useIsMobile();
  const perceptionEnabled = usePerceptionEnabled() && !isMobile;
  const experimentIds = perceptionEnabled ? [ExperimentSpecs.PricingPerceptionFirstYear] : [];
  const {
    isV4
  } = useResultsPageConfig();
  return /*#__PURE__*/React.createElement(DomainsApiClient.Provider, null, /*#__PURE__*/React.createElement(DeclaredExperimentsProvider, {
    ids: experimentIds
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers,
      resolveCorrectedDomains: isV4
    },
    statusesCustomComponents: {
      customConfiguration
    }
  }, /*#__PURE__*/React.createElement(AnonymousResultsPageContent, {
    deviceType: deviceType,
    showCartHeader: showCartHeader
  }))));
};
export const AnonymousResultsPageContent = _ref2 => {
  let {
    deviceType,
    showCartHeader = false
  } = _ref2;
  const isMobile = useIsMobile();
  const isWixHpReferral = useIsWixHpReferral();
  const isWixHpMobile = isMobile && isWixHpReferral;
  const handleBackClick = useResultsBack();
  const {
    isV4
  } = useResultsPageConfig();
  const contentClassName = clsx(s.centeredContent, isV4 && s.narrowContent);
  const {
    taxesNotIncluded
  } = useRecommendations({
    enableOnEmptyQuery: false
  });
  const perception = usePerceptionPricing();
  const {
    heroLayouts,
    ...perceptionPricing
  } = isMobile ? NO_PERCEPTION : perception;
  return /*#__PURE__*/React.createElement(ResultsLayout, {
    biViewMode: "Storefront",
    querySubmitPath: "/",
    deviceType: deviceType,
    showCartHeader: showCartHeader,
    hideSearchBarShadowOnPartialMatch: true,
    showSearchIcon: true,
    hideFilterChips: true,
    useCompactRecommendationsLayout: true,
    headerTop: isV4 && !isMobile ? WIX_HEADER_HEIGHT : undefined,
    headerPadding: isV4 && !isMobile ? '0 48px' : undefined,
    headerMargin: isV4 && isMobile ? '0' : undefined,
    desktopGridGap: isV4 ? 'SP2 0' : undefined,
    voucherPlacement: isV4 ? 'banner' : 'outsideStickyHeader',
    mobileVoucherInSearchBar: isV4,
    optimizedSearchBarStyle: !isV4,
    searchBar: /*#__PURE__*/React.createElement(ResultsSearchBar, {
      isWixHpMobile: isWixHpMobile,
      compactLayouts: isV4
    }),
    header: isV4 ? undefined : /*#__PURE__*/React.createElement(AnonymousResultsHeader, {
      onBackClick: isMobile ? undefined : handleBackClick,
      hideTrustBuilders: true
    }),
    voucher: /*#__PURE__*/React.createElement(AnonymousVoucherBanner, {
      optimizedVoucherCopy: true
    }),
    footer: /*#__PURE__*/React.createElement(Box, {
      gridArea: "footer",
      padding: "SP6 48px",
      className: contentClassName,
      textAlign: "center",
      align: "center"
    }, /*#__PURE__*/React.createElement(AnonymousFooter, {
      taxesNotIncluded: taxesNotIncluded
    })),
    mobileBackButton: isV4 || isWixHpMobile ? undefined : /*#__PURE__*/React.createElement(Box, {
      width: "100%"
    }, /*#__PURE__*/React.createElement(BackButton, {
      onClick: handleBackClick
    }))
  }, /*#__PURE__*/React.createElement(SearchRecommendationModule.Provider, {
    Layouts: {
      customConfiguration: heroLayouts ?? {}
    }
  }, /*#__PURE__*/React.createElement(ResultsRecommendations, _extends({
    showHeroCard: true,
    className: contentClassName,
    emptyState: isWixHpMobile ? /*#__PURE__*/React.createElement(React.Fragment, null) : undefined,
    compactPriceLabel: isV4,
    stackedMobileCards: isV4,
    desktopContentGap: isV4 ? 'SP4' : undefined,
    insightsPaddingTop: isV4 ? 'SP4' : undefined
  }, perceptionPricing))));
};
//# sourceMappingURL=AnonymousResultsPage.js.map